import { useCallback, useEffect, useRef, useState } from 'react';
import useBodyScrollLock from './useBodyScrollLock';
import useHoverOpenIntent from './useHoverOpenIntent';

/**
 * Owns the featured nav bar's mega-menu lifecycle: which menu (if any) is
 * open, which menus have ever been mounted, hover-intent open timing, and
 * body scroll lock while a menu is open. Also exposes handleClose via
 * closeMegaMenuRef so a sibling control (the drawer's hamburger button) can
 * dismiss an open mega menu before opening the drawer.
 *
 * @param {NavigationItem[]} featuredItems
 * @param {{ current: (() => void)|null }} [closeMegaMenuRef]
 * @returns {{
 *   openMegaMenuId: string|null,
 *   megaMenuItems: NavigationItem[],
 *   hoverIntent: { onMouseEnter: () => void, onMouseLeave: () => void, onFocus: () => void, onBlur: () => void },
 *   handleClose: () => void,
 *   handleItemClick: (item: NavigationItem) => void,
 *   handleItemHoverEnter: (item: NavigationItem) => void,
 *   handleFocusLeave: (itemId: string) => void
 * }}
 */
const useFeaturedMegaMenuState = (featuredItems, closeMegaMenuRef) => {
	const [openMegaMenuId, setOpenMegaMenuId] = useState(null);

	// Ids of mega menus that have been opened at least once. A mega menu is
	// only rendered (see megaMenuItems below) once its id lands here — lazily
	// mounting avoids fetching/rendering every featured item's mega menu
	// content (including any embedded fragments) up front, before the user
	// has opened any of them. Never pruned on close, so re-opening a menu
	// doesn't re-trigger its mount-time work (e.g. fragment fetches).
	const [mountedMenuIds, setMountedMenuIds] = useState(new Set());

	const hoverItemIdRef = useRef(null);

	const handleClose = useCallback(() => {
		setOpenMegaMenuId(null);
	}, []);

	// Closes a specific menu, but only if it's *still* the open one.
	//
	// Without this check, here's the bug it prevents: say Menu A is open and
	// the user clicks Button B to open Menu B. That one click does two
	// things: closes A (because focus moved away from A's button) and opens
	// B (because B was clicked). Those two things aren't guaranteed to
	// happen in that order -- if "close A" blindly set "no menu open" right
	// after "open B" had already been applied, it would immediately close
	// the menu the user just opened, and the click would look like it did
	// nothing.
	//
	// The fix: this update doesn't blindly set "no menu open" -- it only
	// closes if the open menu, checked at the moment this actually runs, is
	// still this same one (itemId). By the time it runs, "open B" has
	// already happened, so it sees B is open (not A), and correctly leaves
	// it alone.
	const handleFocusLeave = useCallback((itemId) => {
		setOpenMegaMenuId((prev) => (prev === itemId ? null : prev));
	}, []);

	const handleOpen = useCallback((itemId) => {
		setOpenMegaMenuId(itemId);
		setMountedMenuIds((ids) => {
			if (ids.has(itemId)) return ids;
			return new Set(ids).add(itemId);
		});
	}, []);

	const handleHoverOpen = useCallback(() => {
		if (hoverItemIdRef.current) handleOpen(hoverItemIdRef.current);
	}, [handleOpen]);

	const handleItemClick = useCallback((item) => {
		setOpenMegaMenuId((prev) => {
			const next = prev === item.id ? null : item.id;
			if (next && item.columns?.length > 0) {
				setMountedMenuIds((ids) => {
					if (ids.has(next)) return ids;
					return new Set(ids).add(next);
				});
			}
			return next;
		});
	}, []);

	const hoverIntent = useHoverOpenIntent({ onOpen: handleHoverOpen });

	const handleItemHoverEnter = useCallback(
		(item) => {
			hoverItemIdRef.current = item.id;
			hoverIntent.onMouseEnter();
		},
		[hoverIntent]
	);

	useBodyScrollLock(openMegaMenuId !== null);

	useEffect(() => {
		if (closeMegaMenuRef) {
			// eslint-disable-next-line no-param-reassign -- ref.current assignment is idiomatic React
			closeMegaMenuRef.current = handleClose;
		}
	}, [closeMegaMenuRef, handleClose]);

	const megaMenuItems = featuredItems.filter(
		(item) => mountedMenuIds.has(item.id) && item.columns?.length > 0
	);

	return {
		openMegaMenuId,
		megaMenuItems,
		hoverIntent,
		handleClose,
		handleItemClick,
		handleItemHoverEnter,
		handleFocusLeave
	};
};

export default useFeaturedMegaMenuState;
